/* eslint-disable no-unused-vars */
// eslint-disable-next-line no-underscore-dangle
import axios from "axios";
import qs from "qs";
import config from "../config/config";
import logoutAndClearData from "../utils/utils";
import StorageService from "../utils/storageService";

const { API_BASE_URL } = config;

// Store showError globally (will be set from your app)
let globalShowError = null;

// Function to set the showError function
export const setGlobalShowError = (showError) => {
  globalShowError = showError;
};

const loadApiRequestAuthInterceptor = (instance) => {
  const newAuthInterceptor = instance.interceptors.request.use((req) => {
    const token = StorageService.getItem("accessToken");

    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }

    // Add client-type header from localStorage
    // const clientId = StorageService.getItem("clientId");
    // if (clientId) {
    //   const clientType =
    //     clientId === "shareholder-cv" ? "shareholder_cv" : "shareholder_pv";
    //   req.headers["client-type"] = clientType;
    // }
    return req;
  });
  return newAuthInterceptor;
};

const add401ResponseInterceptor = (instance) => {
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const accessToken = StorageService.getItem("accessToken");
      const refreshToken = StorageService.getItem("refreshToken");

      // Build full request URL for matching
      const requestUrl = `${originalRequest.baseURL || ""}${
        originalRequest.url || ""
      }`;

      if (
        error.response &&
        error.response.status === 401 &&
        // eslint-disable-next-line no-underscore-dangle
        !originalRequest._retry &&
        refreshToken &&
        requestUrl.startsWith(config.API_BASE_URL)
      ) {
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const response = await instance.post(
            `/user/token/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const {
            access_token: newAccessToken,
            refresh_token: newRefreshToken,
          } = response.data.tokens;

          StorageService.setItem("accessToken", newAccessToken);
          StorageService.setItem("refreshToken", newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);

          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          console.error("Token refresh failed:", refreshError);

          if (
            globalShowError &&
            error.response?.data?.error_type === "user_not_found"
          ) {
            globalShowError({
              contextText:
                "Your account hasn't been added by the admin yet. Please contact support.",
              onClose: async () => {
                await logoutAndClearData();
                window.location.href = "/";
              },
            });
          } else {
            await logoutAndClearData();
            window.location.href = "/";
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: "application/json" },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
});

add401ResponseInterceptor(API);
loadApiRequestAuthInterceptor(API);

// eslint-disable-next-line import/prefer-default-export
export { API };
